import React, { useState } from 'react';
import { PieChart, BarChartHorizontal, Laptop, ListFilter, Sun, Moon, Lightbulb } from 'lucide-react';
import { formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения структуры энергопотребления
 */
const UsageBreakdown = ({ results, formData, appliancesList }) => {
  const [viewMode, setViewMode] = useState('chart'); // 'chart' или 'list'

  if (!results) return null;

  // Отображаем распределение по тарифным зонам для многотарифного учета
  const showZoneBreakdown = formData.tariffType !== 'single';
  
  // Отображаем распределение по приборам, если указаны приборы
  const showAppliancesBreakdown = results.appliancesConsumption && results.appliancesConsumption.length > 0;

  // Если нет данных для отображения, возвращаем null
  if (!showZoneBreakdown && !showAppliancesBreakdown) return null;

  // Сортировка приборов по потреблению (по убыванию)
  const sortedAppliances = showAppliancesBreakdown 
    ? [...results.appliancesConsumption].sort((a, b) => b.consumption - a.consumption)
    : [];

  // Цветовая схема для диаграмм и графиков
  const colors = {
    day: 'rgb(251 191 36)', // amber-400
    night: 'rgb(79 70 229)', // indigo-600
    peak: 'rgb(220 38 38)', // red-600
    semipeak: 'rgb(234 179 8)', // yellow-500
    offpeak: 'rgb(79 70 229)', // indigo-600
    appliance: 'rgb(34 197 94)' // green-500
  };

  // Функция для определения иконки прибора
  const getApplianceIcon = (applianceId) => {
    // Здесь можно реализовать сопоставление с нужными иконками, 
    // но для простоты возвращаем общую иконку
    return <Laptop className="w-4 h-4 text-gray-500" />;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <PieChart className="w-5 h-5 mr-2 text-yellow-500" />
          Структура энергопотребления
        </h3>

        {/* Переключатель режима отображения */}
        <div className="flex bg-gray-100 dark:bg-gray-700 rounded-md p-0.5">
          <button
            onClick={() => setViewMode('chart')}
            className={`py-1 px-3 text-xs font-medium rounded ${
              viewMode === 'chart'
                ? 'bg-white dark:bg-gray-600 text-gray-800 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
            }`}
          >
            <BarChartHorizontal className="w-4 h-4 inline mr-1" />
            График
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`py-1 px-3 text-xs font-medium rounded ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-600 text-gray-800 dark:text-white shadow-sm'
                : 'text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white'
            }`}
          >
            <ListFilter className="w-4 h-4 inline mr-1" />
            Список
          </button>
        </div>
      </div>

      {/* Отображение по тарифным зонам */}
      {showZoneBreakdown && (
        <div className="mb-6">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Распределение по тарифным зонам
          </h4>

          {viewMode === 'chart' ? (
            <div className="energy-chart">
              {formData.tariffType === 'day_night' ? (
                <div className="space-y-3">
                  <div className="relative">
                    <div className="flex items-center mb-1">
                      <Sun className="w-4 h-4 text-amber-400 mr-2" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">День (7:00-23:00)</span>
                      <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                        {formatNumber(results.consumption.day, 1)} кВт⋅ч ({Math.round((results.consumption.day / results.totalConsumption) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bar bg-amber-400 dark:bg-amber-500 h-3 rounded-full" 
                        style={{ width: `${(results.consumption.day / results.totalConsumption) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="flex items-center mb-1">
                      <Moon className="w-4 h-4 text-indigo-400 mr-2" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Ночь (23:00-7:00)</span>
                      <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                        {formatNumber(results.consumption.night, 1)} кВт⋅ч ({Math.round((results.consumption.night / results.totalConsumption) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bar bg-indigo-400 dark:bg-indigo-500 h-3 rounded-full" 
                        style={{ width: `${(results.consumption.night / results.totalConsumption) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="relative">
                    <div className="flex items-center mb-1">
                      <div className="w-4 h-4 bg-red-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Пиковая зона</span>
                      <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                        {formatNumber(results.consumption.peak, 1)} кВт⋅ч ({Math.round((results.consumption.peak / results.totalConsumption) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bar bg-red-500 dark:bg-red-600 h-3 rounded-full" 
                        style={{ width: `${(results.consumption.peak / results.totalConsumption) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="flex items-center mb-1">
                      <div className="w-4 h-4 bg-yellow-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Полупиковая зона</span>
                      <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                        {formatNumber(results.consumption.semipeak, 1)} кВт⋅ч ({Math.round((results.consumption.semipeak / results.totalConsumption) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bar bg-yellow-500 dark:bg-yellow-600 h-3 rounded-full" 
                        style={{ width: `${(results.consumption.semipeak / results.totalConsumption) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  <div className="relative">
                    <div className="flex items-center mb-1">
                      <div className="w-4 h-4 bg-indigo-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Внепиковая зона</span>
                      <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                        {formatNumber(results.consumption.offpeak, 1)} кВт⋅ч ({Math.round((results.consumption.offpeak / results.totalConsumption) * 100)}%)
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                      <div 
                        className="bar bg-indigo-500 dark:bg-indigo-600 h-3 rounded-full" 
                        style={{ width: `${(results.consumption.offpeak / results.totalConsumption) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {formData.tariffType === 'day_night' ? (
                <>
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg border border-amber-100 dark:border-amber-800">
                    <div className="flex items-center mb-1">
                      <Sun className="w-4 h-4 text-amber-500 mr-2" />
                      <span className="text-sm font-medium text-amber-700 dark:text-amber-300">День</span>
                    </div>
                    <span className="text-xl font-semibold text-amber-800 dark:text-amber-200 block mb-1">
                      {formatNumber(results.consumption.day, 1)} кВт⋅ч
                    </span>
                    <span className="text-sm text-amber-600 dark:text-amber-400">
                      {Math.round((results.consumption.day / results.totalConsumption) * 100)}% от общего
                    </span>
                  </div>
                  
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
                    <div className="flex items-center mb-1">
                      <Moon className="w-4 h-4 text-indigo-500 mr-2" />
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Ночь</span>
                    </div>
                    <span className="text-xl font-semibold text-indigo-800 dark:text-indigo-200 block mb-1">
                      {formatNumber(results.consumption.night, 1)} кВт⋅ч
                    </span>
                    <span className="text-sm text-indigo-600 dark:text-indigo-400">
                      {Math.round((results.consumption.night / results.totalConsumption) * 100)}% от общего
                    </span>
                  </div>
                </>
              ) : (
                <>
                  <div className="bg-red-50 dark:bg-red-900/20 p-3 rounded-lg border border-red-100 dark:border-red-800">
                    <div className="flex items-center mb-1">
                      <div className="w-3 h-3 bg-red-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-red-700 dark:text-red-300">Пиковая</span>
                    </div>
                    <span className="text-xl font-semibold text-red-800 dark:text-red-200 block mb-1">
                      {formatNumber(results.consumption.peak, 1)} кВт⋅ч
                    </span>
                    <span className="text-sm text-red-600 dark:text-red-400">
                      {Math.round((results.consumption.peak / results.totalConsumption) * 100)}% от общего
                    </span>
                  </div>
                  
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg border border-yellow-100 dark:border-yellow-800">
                    <div className="flex items-center mb-1">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Полупиковая</span>
                    </div>
                    <span className="text-xl font-semibold text-yellow-800 dark:text-yellow-200 block mb-1">
                      {formatNumber(results.consumption.semipeak, 1)} кВт⋅ч
                    </span>
                    <span className="text-sm text-yellow-600 dark:text-yellow-400">
                      {Math.round((results.consumption.semipeak / results.totalConsumption) * 100)}% от общего
                    </span>
                  </div>
                  
                  <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-100 dark:border-indigo-800">
                    <div className="flex items-center mb-1">
                      <div className="w-3 h-3 bg-indigo-500 rounded-full mr-2"></div>
                      <span className="text-sm font-medium text-indigo-700 dark:text-indigo-300">Внепиковая</span>
                    </div>
                    <span className="text-xl font-semibold text-indigo-800 dark:text-indigo-200 block mb-1">
                      {formatNumber(results.consumption.offpeak, 1)} кВт⋅ч
                    </span>
                    <span className="text-sm text-indigo-600 dark:text-indigo-400">
                      {Math.round((results.consumption.offpeak / results.totalConsumption) * 100)}% от общего
                    </span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {/* Отображение по электроприборам */}
      {showAppliancesBreakdown && (
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Потребление по электроприборам
          </h4>

          {viewMode === 'chart' ? (
            <div className="energy-chart space-y-2">
              {sortedAppliances.map((appliance, index) => (
                <div key={appliance.id} className="relative">
                  <div className="flex items-center mb-1">
                    {getApplianceIcon(appliance.id)}
                    <span className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300">{appliance.name}</span>
                    <span className="ml-auto text-sm text-gray-500 dark:text-gray-400">
                      {formatNumber(appliance.consumption, 1)} кВт⋅ч ({formatNumber(appliance.percent, 1)}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3">
                    <div 
                      className="bar bg-green-500 dark:bg-green-600 h-3 rounded-full" 
                      style={{ width: `${appliance.percent}%` }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {sortedAppliances.map((appliance) => (
                <div 
                  key={appliance.id} 
                  className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-100 dark:border-green-800"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center">
                      <Lightbulb className="w-4 h-4 text-green-500 mr-2" />
                      <span className="text-sm font-medium text-green-700 dark:text-green-300">{appliance.name}</span>
                    </div>
                    <span className="text-xs text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-800/50 py-0.5 px-1.5 rounded">
                      {formatNumber(appliance.percent, 1)}%
                    </span>
                  </div>
                  <span className="text-lg font-semibold text-green-800 dark:text-green-200 block mt-1">
                    {formatNumber(appliance.consumption, 1)} кВт⋅ч
                  </span>
                  <div className="text-xs text-green-600 dark:text-green-400 mt-1 space-x-2">
                    <span>{appliance.power} Вт</span>
                    <span>⋅</span>
                    <span>{appliance.quantity} шт.</span>
                    <span>⋅</span>
                    <span>{appliance.hoursPerDay} ч/день</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default UsageBreakdown;